import React from "react"
import {
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	TextField,
	Typography,
} from "@mui/material"
import {Controller, useForm, FormProvider} from "react-hook-form"
import {zodResolver} from "@hookform/resolvers/zod"

import {offerFormSchemaV2, OfferFormDataV2} from "./offerFormValidation"
import {PaymentOptionsSelector} from "./PaymentOptionsSelector"
import {OfferPeriodCard} from "./OfferPeriodCard"

import {api} from "@indebted/api"
import {useNotification} from "@indebted/components/Notification"
import {useAgent} from "@indebted/hooks/Agent"

type CreateOfferModalV2Props = {
	customerID: string
	onClose: () => void
	onSuccess: () => void
}

function CreateOfferModalV2({customerID, onClose, onSuccess}: CreateOfferModalV2Props) {
	const {notification} = useNotification()
	const [agentEmail] = useAgent()
	const [loading, setLoading] = React.useState(false)
	const [offerID] = React.useState(crypto.randomUUID())

	const methods = useForm<OfferFormDataV2>({
		resolver: zodResolver(offerFormSchemaV2),
		defaultValues: {
			startDate: "",
			endDate: "",
			notes: "",
			oneOff: undefined,
			paymentPlan: {
				tiers: [],
			},
		},
	})

	const {control, handleSubmit, trigger} = methods

	const onSubmit = async (data: OfferFormDataV2) => {
		setLoading(true)
		try {
			const tiers: any[] = []

			if (data.oneOff?.discountRate !== undefined) {
				tiers.push({
					tierId: crypto.randomUUID(),
					tierType: "ONE_OFF",
					discountRate: data.oneOff.discountRate / 100,
				})
			}

			if (data.paymentPlan?.tiers) {
				data.paymentPlan.tiers.forEach((tier) => {
					tiers.push({
						tierId: tier.id,
						tierType: "PLAN",
						discountRate: tier.discountRate / 100,
						timeFrameWeeks: tier.maxTimeToPayWeeks,
					})
				})
			}

			await api.customer.createOffer(customerID, {
				offerId: offerID,
				startDate: data.startDate,
				endDate: data.endDate,
				tiers,
				agentEmail: agentEmail || undefined,
				notes: data.notes || undefined,
			})

			notification.success("Customer offer created successfully")
			onSuccess()
		} catch (error: any) {
			notification.error(error.Message || "Failed to create customer offer")
		} finally {
			setLoading(false)
		}
	}

	const handleCreateOffer = async () => {
		const isValid = await trigger()
		if (isValid) {
			handleSubmit(onSubmit)()
		}
	}

	return (
		<Dialog open onClose={onClose} maxWidth="md" fullWidth>
			<DialogTitle sx={{pb: 0}}>
				<Typography sx={{fontSize: "1.25rem", fontWeight: 500, color: "rgba(0,0,0,0.87)", lineHeight: "2rem"}}>
					Create new offer
				</Typography>
				<Typography sx={{fontSize: "0.875rem", fontWeight: 400, color: "#717182", lineHeight: "1.25rem"}}>
					Set up a new payment offer with one or more payment options.
				</Typography>
			</DialogTitle>
			<FormProvider {...methods}>
				<form onSubmit={handleSubmit(onSubmit)}>
					<DialogContent>
						<OfferPeriodCard />

						<PaymentOptionsSelector />

						<Box mt={3}>
							<Typography variant="h6" gutterBottom>
								Internal notes
							</Typography>
							<Typography variant="body2" color="textSecondary" paragraph>
								For agent reference only (not visible to customer).
							</Typography>
							<Controller
								name="notes"
								control={control}
								render={({field}) => (
									<TextField
										{...field}
										placeholder="Add relevant info about this offer"
										multiline
										minRows={3}
										fullWidth
										size="small"
									/>
								)}
							/>
						</Box>
					</DialogContent>
					<DialogActions>
						<Button
							onClick={onClose}
							disabled={loading}
							variant="outlined"
							sx={{
								textTransform: "uppercase",
								borderColor: "#474CDE",
								color: "#474CDE",
								backgroundColor: "white",
								"&:hover": {borderColor: "#3539b5", backgroundColor: "rgba(71, 76, 222, 0.04)"},
							}}
						>
							Cancel
						</Button>
						<Button
							type="button"
							color="primary"
							variant="contained"
							disabled={loading}
							onClick={handleCreateOffer}
						>
							{loading ? <CircularProgress size={24} /> : "Create Offer"}
						</Button>
					</DialogActions>
				</form>
			</FormProvider>
		</Dialog>
	)
}

export {CreateOfferModalV2}
